import type {
  DividerHardware,
  DividerPolicy,
  DividersSetByHand,
  LoaderMode,
} from "@CommonTypes/backend/PspRules";
import { loaderShelfKey } from "@CommonUtils/pog/dividerPolicy";
import { normalizeLoaderDepthValue } from "./lengths";
import {
  inListOrder,
  tellOFacts,
  tellOLoaderEvidence,
  tellOTicks,
  type TellOContext,
} from "./facts";
import type {
  RuleProposal,
  TellOCannotProposal,
  TellOFeature,
  TellOGroup,
  TellOProductsOp,
  TellOReading,
  TellORuleProposal,
  TellOShelfProposal,
} from "./proposal";

/** A.5 Undo: O's base default. Keep off, mode products, sizes from the base. */
export const TELL_O_DEFAULT_POLICY: DividerPolicy = { source: "base" };

const nextSet = (
  current: ReadonlySet<string>,
  op: TellOProductsOp,
  group: readonly string[],
  all: readonly string[],
): Set<string> => {
  if (op === "all") return new Set(all);
  if (op === "only") return new Set(group);
  if (op === "except") {
    const next = new Set(current);
    for (const code of group) next.delete(code);
    return next;
  }
  return new Set([...current, ...group]);
};

/**
 * Which products get dividers or spring loaders. The rule becomes a picked
 * list with both columns written, so the untouched one keeps what it showed.
 */
export const proposeTellOProducts = (
  context: TellOContext,
  {
    features,
    op,
    group,
    reading,
    loaderDepth,
    hardware,
  }: {
    features: TellOFeature[];
    op: TellOProductsOp;
    group?: TellOGroup;
    reading?: TellOReading;
    /** A prefill chip's "Spring loaders 1.5 in deep for every product". */
    loaderDepth?: number;
    hardware?: DividerHardware;
  },
): TellORuleProposal => {
  const ticks = tellOTicks(context, context.policy);
  const all = context.products.map((product) => product.productCode);
  const codes = group?.productCodes ?? [];
  const dividers = features.includes("dividers")
    ? nextSet(ticks.dividers, op, codes, all)
    : ticks.dividers;
  const loaders = features.includes("loaders")
    ? nextSet(ticks.loaders, op, codes, all)
    : ticks.loaders;
  return {
    kind: "rule",
    change: { type: "products", features, op, ...(group ? { group } : {}) },
    patch: {
      source: "items",
      productCodes: inListOrder(context.products, dividers),
      loaderProductCodes: inListOrder(context.products, loaders),
      ...(loaderDepth !== undefined ? { loaderDepth } : {}),
      ...(hardware ? { hardware } : {}),
    },
    ...(reading ? { reading } : {}),
  };
};

/** "No dividers" / "No spring loaders": the other column keeps what it showed. */
export const proposeTellOClear = (
  context: TellOContext,
  feature: TellOFeature,
): TellORuleProposal => {
  const ticks = tellOTicks(context, context.policy);
  return {
    kind: "rule",
    change: { type: "clear", feature },
    patch:
      feature === "dividers"
        ? {
            source: "items",
            productCodes: [],
            loaderProductCodes: inListOrder(context.products, ticks.loaders),
            keepFixtureDividers: false,
          }
        : {
            source: "items",
            productCodes: inListOrder(context.products, ticks.dividers),
            loaderProductCodes: [],
            loaderMode: "products",
          },
  };
};

/**
 * "Dividers like the base planogram". Source "base" would also restore the base's loader
 * products, so unless they are already the ticked ones the base's dividers become a picked list.
 */
export const proposeTellOLikeBase = (context: TellOContext): TellORuleProposal => {
  const loaders = tellOTicks(context, context.policy).loaders;
  const base = tellOTicks(context, { source: "base" });
  const sameLoaders =
    loaders.size === base.loaders.size && [...loaders].every((code) => base.loaders.has(code));
  return {
    kind: "rule",
    change: { type: "like-base" },
    patch: sameLoaders
      ? { source: "base" }
      : {
          source: "items",
          productCodes: inListOrder(context.products, base.dividers),
          loaderProductCodes: inListOrder(context.products, loaders),
        },
  };
};

/** One divider size: the width changes, the rest of the size in use stays. */
export const proposeTellOWidth = (context: TellOContext, width: number): RuleProposal => {
  const current = tellOFacts(context, context.policy).hardware;
  if (!(width > 0)) return { kind: "cannot", reason: { code: "not-understood" } };
  return {
    kind: "rule",
    change: { type: "width", width, previousWidth: current?.width ?? null },
    patch: {
      hardware: {
        width,
        height: current?.height ?? 0,
        depth: current?.depth ?? 0,
        atStart: current?.atStart ?? true,
        atEnd: current?.atEnd ?? true,
      },
    },
  };
};

/** The deepest loader below a shelf: a quarter of the unit under it. */
export const deepestLoaderBelow = (shelfDepth: number) =>
  Math.max(0, Math.floor((shelfDepth - 1) / 25) * 25);

/** The flow's loader depth; it must stay below the shallowest base shelf. */
export const proposeTellODepth = (context: TellOContext, depth: number): RuleProposal => {
  if (!(depth > 0)) return { kind: "cannot", reason: { code: "not-understood" } };
  const shallowest = tellOLoaderEvidence(context).shallowestShelfDepth;
  if (shallowest !== null && shallowest > 0 && depth >= shallowest) {
    const deepest = deepestLoaderBelow(shallowest);
    return {
      kind: "cannot",
      reason: { code: "depth-too-deep", depth, shallowest, deepest },
      ...(deepest > 0 ? { nearest: depthProposal(context, deepest) } : {}),
    };
  }
  return depthProposal(context, depth);
};

const depthProposal = (context: TellOContext, depth: number): TellORuleProposal => {
  const facts = tellOFacts(context, context.policy);
  return {
    kind: "rule",
    change: { type: "depth", depth, previousDepth: facts.loaderDepths[0] ?? null },
    patch: { loaderDepth: depth },
  };
};

export const proposeTellOKeep = (keep: boolean): TellORuleProposal => ({
  kind: "rule",
  change: { type: "keep", keep },
  patch: { keepFixtureDividers: keep },
});

export const proposeTellOLoaderMode = (mode: LoaderMode): TellORuleProposal => ({
  kind: "rule",
  change: { type: "loader-mode", mode },
  patch: { loaderMode: mode },
});

/** Bays and the most shelves in one bay, numbered as the requests number them. */
export const tellOBaseShelfLayout = (context: TellOContext) => {
  const evidence = tellOLoaderEvidence(context);
  let bays = 0;
  let shelves = 0;
  for (const shelf of evidence.shelvesByPosition.values()) {
    bays = Math.max(bays, shelf.bayNo);
    shelves = Math.max(shelves, shelf.shelfNo);
  }
  return { evidence, bays, shelves };
};

/**
 * One store shelf set by hand. The rules window has no store, so the base
 * planogram's store unless the text names one.
 */
export const proposeTellOShelf = (
  context: TellOContext,
  {
    bayNo,
    shelfNo,
    storeCode,
    ...setting
  }: { bayNo: number; shelfNo: number; storeCode?: string | null } & (
    | { feature: "dividers"; dividersSetByHand: DividersSetByHand | null }
    | { feature: "loaders"; loaderDepth: number | null }
  ),
): TellOShelfProposal | TellOCannotProposal => {
  const { evidence, bays, shelves } = tellOBaseShelfLayout(context);
  const baseShelf = evidence.shelvesByPosition.get(loaderShelfKey(bayNo, shelfNo));
  if (!baseShelf) {
    return { kind: "cannot", reason: { code: "shelf-not-found", bayNo, shelfNo, bays, shelves } };
  }
  const code = storeCode?.trim() || context.baseStoreCode;
  if (!code) {
    return {
      kind: "cannot",
      reason: { code: "every-store-override", bayNo, shelfNo, storeCode: null },
    };
  }
  const isBaseStore = code === context.baseStoreCode;
  const target = { kind: "shelf" as const, storeCode: code, isBaseStore, bayNo, shelfNo };
  if (setting.feature === "dividers") {
    const row = isBaseStore
      ? context.fixtureRows?.find((fixture) => fixture.bayNo === bayNo && fixture.shelfNo === shelfNo)
      : undefined;
    return {
      ...target,
      feature: "dividers",
      dividersSetByHand: setting.dividersSetByHand,
      ...(row ? { previous: row.dividersSetByHand } : {}),
    };
  }
  const depth = setting.loaderDepth;
  if (depth !== null && depth > 0 && baseShelf.physicalDepth > 0 && depth >= baseShelf.physicalDepth) {
    const deepest = deepestLoaderBelow(baseShelf.physicalDepth);
    return {
      kind: "cannot",
      reason: { code: "depth-too-deep", depth, shallowest: baseShelf.physicalDepth, deepest },
      ...(deepest > 0
        ? { nearest: { ...target, feature: "loaders" as const, loaderDepth: deepest } }
        : {}),
    };
  }
  const row = isBaseStore
    ? context.libraryShelves?.find((shelf) => shelf.bayNo === bayNo && shelf.shelfNo === shelfNo)
    : undefined;
  return {
    ...target,
    feature: "loaders",
    loaderDepth: depth,
    ...(isBaseStore ? { previous: normalizeLoaderDepthValue(row?.loaderDepth) } : {}),
  };
};

/** Products on the base planogram's top (or bottom, or n-th) shelf of every bay. */
export const tellOPositionGroup = (
  context: TellOContext,
  position: "top" | "bottom" | number,
): Extract<TellOGroup, { kind: "position" }> => {
  const { evidence } = tellOBaseShelfLayout(context);
  // Top and bottom are the bay's highest and lowest shelves holding products,
  // so an empty top shelf does not hide the bay's real top row.
  const firstByBay = new Map<number, number>();
  const lastByBay = new Map<number, number>();
  for (const shelf of evidence.shelvesByPosition.values()) {
    if (shelf.counted) {
      firstByBay.set(
        shelf.bayNo,
        Math.min(firstByBay.get(shelf.bayNo) ?? shelf.shelfNo, shelf.shelfNo),
      );
      lastByBay.set(shelf.bayNo, Math.max(lastByBay.get(shelf.bayNo) ?? 0, shelf.shelfNo));
    }
  }
  const codes = new Set<string>();
  for (const shelf of evidence.shelvesByPosition.values()) {
    if (!shelf.counted) continue;
    const wanted =
      position === "top"
        ? firstByBay.get(shelf.bayNo)
        : position === "bottom"
          ? lastByBay.get(shelf.bayNo)
          : position;
    if (shelf.shelfNo !== wanted) continue;
    for (const item of shelf.items) codes.add(item.productCode);
  }
  return { kind: "position", position, productCodes: inListOrder(context.products, codes) };
};

/** "Dividers only on the top shelf in every store": the nearest thing is those products. */
export const proposeTellOShelfPosition = (
  context: TellOContext,
  position: "top" | "bottom" | number,
  features: TellOFeature[] = ["dividers"],
): TellOCannotProposal => {
  const group = tellOPositionGroup(context, position);
  return {
    kind: "cannot",
    reason: {
      code: "shelf-position",
      feature: features.length === 1 ? features[0] : "dividers",
      position,
      products: group.productCodes.length,
    },
    ...(group.productCodes.length > 0
      ? { nearest: proposeTellOProducts(context, { features, op: "only", group }) }
      : {}),
  };
};

/**
 * The rule after Apply. A shelf proposal is a PUT, not a rule change, and
 * leaves the rule as it is; so do questions and cannot cards.
 */
export const applyTellOProposal = (policy: DividerPolicy, proposal: RuleProposal): DividerPolicy => {
  if (proposal.kind === "undo") return { ...TELL_O_DEFAULT_POLICY };
  if (proposal.kind !== "rule") return policy;
  const merged: DividerPolicy = { ...policy, ...proposal.patch };
  if (merged.source === "base") {
    delete merged.productCodes;
    delete merged.loaderProductCodes;
  }
  return merged;
};
